import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, Edit2, Trash2, Search, AlertTriangle, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  furniture: '🪑', electronics: '📺', appliance: '🔌', kitchen: '🍳',
  plumbing: '🚰', electrical: '💡', safety: '🧯', cleaning: '🧹', other: '📦',
};
const CONDITION_STYLES = {
  new:      'bg-green-100 text-green-800',
  good:     'bg-blue-100 text-blue-800',
  fair:     'bg-yellow-100 text-yellow-800',
  damaged:  'bg-orange-100 text-orange-800',
  disposed: 'bg-red-100 text-red-800',
};

const Assets = () => {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState({ categories: [], conditions: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const qs = [
        propertyFilter && `property_id=${propertyFilter}`,
        categoryFilter && `category=${categoryFilter}`,
        conditionFilter && `condition=${conditionFilter}`,
      ].filter(Boolean).join('&');
      const [aRes, pRes, mRes, sRes] = await Promise.all([
        axios.get(`${API}/assets${qs ? '?' + qs : ''}`, h),
        axios.get(`${API}/properties`, h),
        axios.get(`${API}/assets/meta`, h),
        axios.get(`${API}/assets/summary${propertyFilter ? `?property_id=${propertyFilter}` : ''}`, h),
      ]);
      setAssets(aRes.data);
      setProperties(pRes.data);
      setMeta(mRes.data);
      setSummary(sRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load assets', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token, propertyFilter, categoryFilter, conditionFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/assets/${deleting.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Asset deleted' });
      setDeleting(null); fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const filtered = assets.filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return a.name?.toLowerCase().includes(s) || a.property_name?.toLowerCase().includes(s) || a.notes?.toLowerCase().includes(s);
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="assets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Package /> Assets & Inventory</h1>
          <p className="text-gray-600 mt-1">Track furniture, electronics, appliances across all properties</p>
        </div>
        <Button data-testid="add-asset-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus size={18} className="mr-2" /> Add Asset
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white">
            <p className="text-blue-100 text-xs">Total Value</p>
            <p className="text-3xl font-bold mt-1">₹{(summary.total_value || 0).toLocaleString()}</p>
          </Card>
          <Card className="p-5 border-l-4 border-l-green-500">
            <p className="text-xs text-gray-500">Total Items</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{summary.total_assets}</p>
            <p className="text-xs text-gray-500 mt-1">{summary.total_items} unique SKU(s)</p>
          </Card>
          <Card className="p-5 border-l-4 border-l-orange-500">
            <p className="text-xs text-gray-500">Damaged / Disposed</p>
            <p className="text-3xl font-bold text-orange-700 mt-1">
              {(summary.by_condition?.damaged || 0) + (summary.by_condition?.disposed || 0)}
            </p>
          </Card>
          <Card className={`p-5 border-l-4 ${summary.expiring_warranties?.length ? 'border-l-red-500 bg-red-50/30' : 'border-l-gray-300'}`}>
            <p className="text-xs text-gray-500">Warranties Expiring (60d)</p>
            <p className="text-3xl font-bold text-red-700 mt-1">{summary.expiring_warranties?.length || 0}</p>
            {summary.expiring_warranties?.length > 0 && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertTriangle size={10} /> {summary.expiring_warranties[0].name}
              </p>
            )}
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="assets-search"
            type="text"
            placeholder="Search by name, property, notes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select data-testid="asset-property-filter" value={propertyFilter} onChange={e => setPropertyFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="asset-category-filter" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">All Categories</option>
          {meta.categories.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
        </select>
        <select data-testid="asset-condition-filter" value={conditionFilter} onChange={e => setConditionFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">All Conditions</option>
          {meta.conditions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="assets-empty">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No assets tracked yet</h3>
          <p className="text-gray-600 mb-4">Add furniture, appliances, electronics to your inventory</p>
          <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-2" /> Add First Asset</Button>
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="assets-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Asset</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Category</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Property</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Qty</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Value</th>
                <th className="p-3 text-center text-xs font-semibold text-gray-700 uppercase">Condition</th>
                <th className="p-3 text-left text-xs font-semibold text-gray-700 uppercase">Warranty</th>
                <th className="p-3 text-right text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-b hover:bg-gray-50" data-testid={`asset-row-${a.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-gray-900">{a.name}</p>
                    {a.notes && <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{a.notes}</p>}
                  </td>
                  <td className="p-3 text-sm">
                    <span className="bg-gray-100 px-2 py-1 rounded-full text-xs">{CATEGORY_ICONS[a.category]} {a.category}</span>
                  </td>
                  <td className="p-3 text-sm text-gray-700">
                    {a.property_name}{a.room_number && ` • Room ${a.room_number}`}
                  </td>
                  <td className="p-3 text-right text-sm font-medium">{a.quantity}</td>
                  <td className="p-3 text-right text-sm font-semibold text-blue-700">₹{((a.purchase_price || 0) * (a.quantity || 1)).toLocaleString()}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${CONDITION_STYLES[a.condition]}`}>{a.condition}</span>
                  </td>
                  <td className="p-3 text-xs text-gray-600">{a.warranty_expiry || '-'}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`asset-edit-${a.id}`} onClick={() => { setEditing(a); setShowForm(true); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 size={14}/></button>
                      <button data-testid={`asset-delete-${a.id}`} onClick={() => setDeleting(a)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <AssetFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          properties={properties}
          meta={meta}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Asset"
        message="Are you sure?"
        itemName={deleting?.name || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

const AssetFormModal = ({ open, onClose, onSuccess, editing, properties, meta, token }) => {
  const [form, setForm] = useState({
    property_id: '', room_id: '', name: '',
    category: 'furniture', quantity: 1, purchase_price: 0,
    purchase_date: '', condition: 'new',
    warranty_expiry: '', notes: '',
  });
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      property_id: editing.property_id || '', room_id: editing.room_id || '',
      name: editing.name || '', category: editing.category || 'furniture',
      quantity: editing.quantity || 1, purchase_price: editing.purchase_price || 0,
      purchase_date: editing.purchase_date || '', condition: editing.condition || 'new',
      warranty_expiry: editing.warranty_expiry || '', notes: editing.notes || '',
    });
  }, [editing]);

  useEffect(() => {
    if (!form.property_id) { setRooms([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/properties/${form.property_id}/rooms`, { headers: { Authorization: `Bearer ${token}` } });
        setRooms(res.data);
      } catch { setRooms([]); }
    })();
  }, [form.property_id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        purchase_price: parseFloat(form.purchase_price) || 0,
        room_id: form.room_id || null,
        purchase_date: form.purchase_date || null,
        warranty_expiry: form.warranty_expiry || null,
      };
      if (editing) {
        await axios.put(`${API}/assets/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Asset updated' });
      } else {
        await axios.post(`${API}/assets`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Asset added' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Asset' : 'Add Asset'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="asset-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Name *</Label><Input data-testid="asset-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Office Chair" required /></div>
            <div>
              <Label>Property *</Label>
              <select data-testid="asset-property" value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value, room_id: ''})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                <option value="">Select</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Room (Optional)</Label>
              <select data-testid="asset-room" value={form.room_id} onChange={e => setForm({...form, room_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled={!form.property_id}>
                <option value="">Common / Unassigned</option>
                {rooms.map(r => <option key={r.id} value={r.id}>Room {r.room_number}</option>)}
              </select>
            </div>
            <div>
              <Label>Category *</Label>
              <select data-testid="asset-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {meta.categories.map(c => <option key={c} value={c}>{CATEGORY_ICONS[c]} {c}</option>)}
              </select>
            </div>
            <div>
              <Label>Condition *</Label>
              <select data-testid="asset-condition" value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {meta.conditions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Quantity *</Label><Input data-testid="asset-qty" type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required /></div>
            <div><Label>Unit Price (₹)</Label><Input data-testid="asset-price" type="number" min="0" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: e.target.value})} /></div>
            <div><Label>Purchase Date</Label><Input data-testid="asset-purchase-date" type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} /></div>
            <div><Label>Warranty Expiry</Label><Input data-testid="asset-warranty" type="date" value={form.warranty_expiry} onChange={e => setForm({...form, warranty_expiry: e.target.value})} /></div>
            <div className="col-span-2"><Label>Notes</Label><textarea data-testid="asset-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="asset-submit" type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Add Asset')}</Button>
            <Button data-testid="asset-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Assets;
